import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {SidebarService} from './sidebar.service';
import {NavigationEnd, Router, RouterLink} from '@angular/router';
import {AsyncPipe, NgClass} from '@angular/common';
import {MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelContent,
  MatExpansionPanelHeader} from '@angular/material/expansion';
import {filter, tap, Subscription} from 'rxjs';
import {BreakpointService} from '../../services/breakpoint/breakpoint.service';
import {SidebarItem, SidebarSection, SidebarSubItem} from '../../../sidebar-section.provider';
import {environment} from 'src/environments/environment';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  imports: [
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    NgClass,
    RouterLink,
    AsyncPipe,
    MatExpansionPanelContent,
  ],
})
export class SidebarComponent implements OnInit, OnDestroy {
  protected readonly sidebarService = inject(SidebarService);
  protected readonly breakpointService = inject(BreakpointService);
  private readonly router = inject(Router);
  private routerEventsSub?: Subscription;

  protected readonly deviceSize$ = inject(BreakpointService).deviceSize$.pipe(
    tap((deviceSize) => {
      this.sidebarService.forceExpand = !deviceSize.isMobile;
    })
  );

  private readonly mobileSidebar = [
    'Dashboard',
    'Your Profile',
    'My Enrolment',
    'Benefits',
    'Claims',
    'Network Hospitals',
    'Marketplace',
    'My Documents',
    'Support',
    'Wellness',
    '2024-2025 claims',
    'Prudent Prime',
  ];

  expandedItems = new Set<SidebarItem>();
  expandedSubItems = new Set<SidebarSubItem>();

  expandedSection?: SidebarSection = undefined;
  chosenSection?: SidebarSection = undefined;

  ngOnInit() {
    this.sidebarService.forceExpand = !this.sidebarService.forceExpand;

    // to remove the focus from the sidebar after clicking on any item
    this.routerEventsSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        const el = document.activeElement;

        if (el instanceof HTMLElement) {
          el.blur();
        }
      });
  }

  ngOnDestroy() {
    this.routerEventsSub?.unsubscribe();
  }

  mobileSizeMenu(sidebar: SidebarSection[], isMobile: boolean) {
    if (isMobile) {
      const mobileSidebar = sidebar[0].items.filter(
        (mobileView) => this.mobileSidebar.includes(mobileView.title) || mobileView.isCustomItem
      );

      sidebar[0].items = [...mobileSidebar];

      return sidebar;
    }

    return sidebar;
  }

  toggleItem(sidebarItem: SidebarItem) {
    if (this.expandedItems.has(sidebarItem)) {
      this.expandedItems.delete(sidebarItem);
    } else {
      this.expandedItems.add(sidebarItem);
    }
  }

  isItemExpanded(sidebarItem: SidebarItem) {
    return this.expandedItems.has(sidebarItem);
  }

  toggleSubItem(subItem: SidebarSubItem) {
    if (this.expandedSubItems.has(subItem)) {
      this.expandedSubItems.delete(subItem);
    } else {
      this.expandedSubItems.add(subItem);
    }
  }

  isSubItemExpanded(subItem: SidebarSubItem) {
    return this.expandedSubItems.has(subItem);
  }

  setChosenSection(sidebarSection: SidebarSection) {
    this.chosenSection = sidebarSection;
  }

  protected sidebarItemTrackBy(index: number, sidebarItem: SidebarItem): string {
    const link = sidebarItem.routerLink?.();

    return link ?? `${sidebarItem.title}-${index}`;
  }

  protected readonly environment = environment;

  icon(sidebarSection: SidebarSection) {
    const url = this.router.url;

    for (const item of sidebarSection.items) {
      if (item.action) {
        continue;
      }

      const link = item.routerLink?.();

      if (!item.sidebarSubItem && link && url.includes(link)) {
        return sidebarSection.activeIcon ? sidebarSection.activeIcon : sidebarSection.icon;
      }

      if (item.sidebarSubItem && item.sidebarSubItem.some((subItem) => (subItem.routerLink &&
         url.includes(subItem.routerLink())) ||
        (subItem.children ?? []).some((child) => child.routerLink && url.includes(child.routerLink())))) {
        return sidebarSection.activeIcon ? sidebarSection.activeIcon : sidebarSection.icon;
      }
    }

    return sidebarSection.icon;
  }

  expandSection(sidebarSection: SidebarSection, sidebarSections: SidebarSection[]) {
    sidebarSections.forEach((section) => {
      section.expanded = section === sidebarSection;
    });
    this.expandedSection = sidebarSection;
  }

  checkSidebarItem(sidebarItem: SidebarItem | SidebarSubItem) {
    if ((sidebarItem as SidebarItem).action) {
      return false;
    }

    if (
      this.router.url.includes('dashboard/enrolment') &&
      sidebarItem.title.includes('My Enrolment')
    ) {
      return true;
    }

    const link = sidebarItem.routerLink?.();

    return link ? this.router.url.includes(link) : false;
  }
}
